import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Check, Loader, CreditCard, Building2, Package, X, FileText } from 'lucide-react';
import logoColor from '../assets/libben-smart.png';
import { useTranslation } from 'react-i18next';
import { theme } from '../styles/theme';
import { formatPrice } from '../utils/currency';
import { COUNTRIES, getDocumentConfig, getCountryCurrency } from '../utils/documents';
import { getPasswordError } from '../utils/password';
import { cognitoAuth } from '../services/cognito';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const GOOGLE_LOGIN_URL = cognitoAuth.getGoogleLoginUrl?.();

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const API_URL = process.env.REACT_APP_API_URL;

// Chama /register sem JWT
async function registerTenant(payload) {
  const res = await fetch(`${API_URL}/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  const data = await res.json();
  if (!res.ok) throw Object.assign(new Error(data.message || 'Error'), { status: res.status });
  return data;
}

// Busca tiers públicos (sem JWT) — endpoint leve que retorna apenas tiers ativos não-trial
async function fetchPublicTiers(audience) {
  try {
    const url = audience
      ? `${API_URL}/public/tiers?audience=${audience}`
      : `${API_URL}/public/tiers`;
    const res = await fetch(url);
    if (!res.ok) return [];
    const data = await res.json();
    return data.tiers || [];
  } catch {
    // Fallback com tiers hardcoded se endpoint não existir ainda
    return [
      { tierId: 'starter', name: 'Starter', audience: 'business', prices: { BRL: { monthly: 297 } }, limits: { conversations: 500, agents: 1, channels: 1 } },
      { tierId: 'growth',  name: 'Growth',  audience: 'business', prices: { BRL: { monthly: 697 } }, limits: { conversations: 2000, agents: 3, channels: 3 } },
      { tierId: 'pro',     name: 'Pro',     audience: 'business', prices: { BRL: { monthly: 1497 } }, limits: { conversations: 8000, agents: -1, channels: -1 } },
    ];
  }
}

export default function RegisterPage() {
  const { t } = useTranslation('register');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pathChosen, setPathChosen] = useState(false);

  // Se vier com ?path=business pula a seleção
  // Se vier com ?email (Google OAuth) pré-preenche mas mantém a tela de seleção
  useEffect(() => {
    if (searchParams.get('path') === 'business') setPathChosen(true);
  }, []); // eslint-disable-line

  const googleEmail = searchParams.get('email') || '';
  const googleName  = searchParams.get('name')  || '';
  if (!pathChosen) {
    return (
      <PathPage>
        <PathGlow />
        <PathContainer>
          <PathLogo src={logoColor} alt="Libben Agents" />
          <PathTitle>{t('pathTitle')}</PathTitle>
          <PathSubtitle>{t('pathSubtitle')}</PathSubtitle>
          <PathCards>
            <PathCard onClick={() => navigate('/welcome')}>
              <PathIcon>🙋</PathIcon>
              <PathCardTitle>{t('pathIndividualTitle')}</PathCardTitle>
              <PathCardDesc>{t('pathIndividualDesc')}</PathCardDesc>
              <PathCardCta>{t('pathIndividualCta')} →</PathCardCta>
            </PathCard>
            <PathCard onClick={() => setPathChosen(true)}>
              <PathIcon>🏢</PathIcon>
              <PathCardTitle>{t('pathBusinessTitle')}</PathCardTitle>
              <PathCardDesc>{t('pathBusinessDesc')}</PathCardDesc>
              <PathCardCta>{t('pathBusinessCta')} →</PathCardCta>
            </PathCard>
          </PathCards>
          {GOOGLE_LOGIN_URL && (
            <>
              <PathDivider><span>{t('orContinueWith', { defaultValue: 'ou continue com' })}</span></PathDivider>
              <PathGoogleBtn href={GOOGLE_LOGIN_URL}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                  <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                </svg>
                {t('signUpWithGoogle', { defaultValue: 'Criar conta com Google' })}
              </PathGoogleBtn>
            </>
          )}
          <PathFooter>
            {t('alreadyHaveAccount')} <Link to="/login">{t('signIn')}</Link>
          </PathFooter>
        </PathContainer>
      </PathPage>
    );
  }

  return stripePromise ? (
    <Elements stripe={stripePromise}>
      <RegisterForm googleEmail={googleEmail} googleName={googleName} />
    </Elements>
  ) : <RegisterForm googleEmail={googleEmail} googleName={googleName} />;
}

function RegisterForm({ googleEmail = '', googleName = '' }) {
  const { t, i18n } = useTranslation('register');
  const { t: tTerms } = useTranslation('terms');
  const navigate = useNavigate();

  const [step, setStep]       = useState(0);
  const [annualBilling, setAnnualBilling] = useState(false);
  const [tiers, setTiers]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [result, setResult]   = useState(null);
  const [isFree, setIsFree]   = useState(false);
  const [pendingEmail, setPendingEmail]     = useState('');
  const [pendingPassword, setPendingPassword] = useState('');
  const [confirmCode, setConfirmCode]       = useState('');
  const [confirming, setConfirming]         = useState(false);
  const [resendLoading, setResendLoading]   = useState(false);
  const [resendMsg, setResendMsg]           = useState('');

  // Verificação de email (step -1 antes do step 0)
  const [emailVerifyStep, setEmailVerifyStep] = useState(true); // true = mostrar verificação
  const [verifyEmail, setVerifyEmail]         = useState(googleEmail);
  const [verifyCode, setVerifyCode]           = useState('');
  const [verifyToken, setVerifyToken]         = useState(googleEmail ? 'google-verified' : ''); // Google já verificado
  const [codeSent, setCodeSent]               = useState(false);

  // Se veio do Google, pula verificação
  React.useEffect(() => {
    if (googleEmail) setEmailVerifyStep(false);
  }, []); // eslint-disable-line

  const [termsOpen, setTermsOpen]         = useState(false);
  const [termsScrolled, setTermsScrolled] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsAcceptedAt, setTermsAcceptedAt] = useState(null);
  const termsBodyRef = useRef(null);

  const handleTermsScroll = () => {
    const el = termsBodyRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) setTermsScrolled(true);
  };

  const [form, setForm] = useState({
    name: googleName, email: googleEmail, phone: '', document: '',
    country: 'BR', personType: 'company',
    city: '', state: '', postalCode: '', addressNumber: '',
    adminName: googleName, adminEmail: googleEmail, adminPassword: '',
    pendingTier: '',
    currency: 'BRL'
  });

  // Idioma dos termos: PT-BR para Brasil, EN para demais países
  const termsLang = form.country === 'BR' ? 'pt-BR' : 'en';
  const termsSections = tTerms('sections', { lng: termsLang, returnObjects: true }) || [];

  useEffect(() => { fetchPublicTiers('business').then(setTiers); }, []);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const validateField = (field, value) => {
    let msg = '';
    if (field === 'email' || field === 'adminEmail') {
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) msg = t('invalidEmail', { defaultValue: 'Email inválido' });
    }
    if (field === 'adminPassword') {
      if (value) msg = getPasswordError(value) || '';
    }
    setFieldErrors(prev => ({ ...prev, [field]: msg }));
  };

  const handleSendCode = async () => {
    if (!verifyEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(verifyEmail)) {
      setError(t('invalidEmail', { defaultValue: 'Email inválido' })); return;
    }
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/auth/send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verifyEmail })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setCodeSent(true);
      // Pré-preenche o email no formulário
      set('email', verifyEmail);
      set('adminEmail', verifyEmail);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleVerifyCode = async () => {
    if (verifyCode.length < 6) { setError(t('invalidCode', { defaultValue: 'Digite o código de 6 dígitos' })); return; }
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/auth/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verifyEmail, code: verifyCode })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setVerifyToken(data.verifyToken);
      setEmailVerifyStep(false);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleNext = () => {
    setError('');
    if (step === 0) {
      if (!form.name || !form.email) { setError(t('validationCompany')); return; }
      if (!form.adminPassword) { setError(t('validationAdmin')); return; }
      if (form.adminPassword.length < 8) { setError(t('adminPasswordPlaceholder')); return; }
      // Garante que adminName/adminEmail espelham os dados da entidade
      set('adminName', form.name);
      set('adminEmail', form.email);
    }
    if (step === 1 && !form.pendingTier) { setError(t('validationPlan')); return; }
    // Free tier: pula direto para o submit ao avançar do step de plano
    if (step === 1 && isFree) { handleSubmitFree(); return; }
    if (step === 1) { setTermsAccepted(false); setTermsScrolled(false); }
    setStep(s => s + 1);
  };

  const stripeHook  = useStripe();
  const elementsHook = useElements();

  // Submit para tiers gratuitos — sem cartão
  const handleSubmitFree = async () => {
    setError('');
    setLoading(true);
    try {
      const docType = getDocumentConfig(form.country, form.personType).type;
      const data = await registerTenant({
        name:          form.name,
        email:         form.email,
        phone:         form.phone,
        document:      form.document.replace(/\D/g, ''),
        documentType:  docType,
        country:       form.country,
        city:          form.city,
        state:         form.state,
        postalCode:    form.postalCode,
        addressNumber: form.addressNumber,
        personType:    form.personType,
        currency:      getCountryCurrency(form.country),
        adminName:     form.adminName || form.name,
        adminEmail:    form.adminEmail || form.email,
        adminPassword: form.adminPassword,
        pendingTier:   form.pendingTier,
        billingAnnual: annualBilling,
        emailVerifyToken: verifyToken,
        termsAcceptedAt: new Date().toISOString(),
        termsVersion:    '1.1'
      });
      setResult(data);
      setPendingEmail(form.adminEmail || form.email);
      setPendingPassword(form.adminPassword);
      setStep(2); // step 2 = confirm email no fluxo free
      try {
        const loginResult = await cognitoAuth.signIn(form.adminEmail || form.email, form.adminPassword);
        if (loginResult.success) {
          localStorage.setItem('user', JSON.stringify(loginResult.user));
          setConfirming(false);
        } else {
          setConfirming(true);
        }
      } catch {
        setConfirming(true);
      }
    } catch (err) {
      if (err.status === 409) setError(t('errorDuplicate'));
      else setError(t('errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError('');
    if (!termsAccepted) { setError(t('validationTerms')); return; }
    if (form.country === 'BR' && (!form.postalCode || !form.addressNumber)) {
      setError(t('validationAddress')); return;
    }

    const currency = getCountryCurrency(form.country);

    setLoading(true);
    try {
      const docType = getDocumentConfig(form.country, form.personType).type;

      // Tokeniza via Stripe.js — nunca envia dados brutos ao backend
      if (!stripeHook || !elementsHook) throw Object.assign(new Error(t('stripeNotLoaded', { defaultValue: 'Stripe não carregado' })), { status: 402 });
      const cardElement = elementsHook.getElement(CardElement);
      const { paymentMethod, error: stripeError } = await stripeHook.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: { name: form.adminName || form.name, email: form.adminEmail }
      });
      if (stripeError) throw Object.assign(new Error(stripeError.message), { status: 402 });
      const creditCard = { token: paymentMethod.id };

      const data = await registerTenant({
        name:          form.name,
        email:         form.email,
        phone:         form.phone,
        document:      form.document.replace(/\D/g, ''),
        documentType:  docType,
        country:       form.country,
        city:          form.city,
        state:         form.state,
        postalCode:    form.postalCode,
        addressNumber: form.addressNumber,
        personType:    form.personType,
        currency,
        adminName:     form.adminName,
        adminEmail:    form.adminEmail,
        adminPassword: form.adminPassword,
        pendingTier:   form.pendingTier,
        billingAnnual: annualBilling,
        paymentMethod: 'credit_card',
        creditCard,
        creditCardHolderInfo: {
          name:          form.adminName || form.name,
          email:         form.adminEmail,
          cpfCnpj:       form.document.replace(/\D/g, ''),
          postalCode:    form.postalCode.replace(/\D/g, ''),
          addressNumber: form.addressNumber,
          phone:         form.phone || ''
        },
        termsAcceptedAt: termsAcceptedAt || new Date().toISOString(),
        termsVersion:    '1.1',
        emailVerifyToken: verifyToken
      });

      setResult(data);
      setPendingEmail(form.adminEmail);
      setPendingPassword(form.adminPassword);
      setStep(STEPS.length - 1);

      try {
        const loginResult = await cognitoAuth.signIn(form.adminEmail, form.adminPassword);
        if (loginResult.success) {
          localStorage.setItem('user', JSON.stringify(loginResult.user));
          setConfirming(false);
        } else {
          setConfirming(true);
        }
      } catch {
        setConfirming(true);
      }

    } catch (err) {
      if (err.status === 409) setError(t('errorDuplicate'));
      else if (err.status === 402) setError(err.message || t('errorCard'));
      else setError(t('errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const selectedTier = tiers.find(t => t.tierId === form.pendingTier);
  const isFreeCalc = selectedTier && (selectedTier.free === true || (selectedTier.prices?.BRL?.monthly === 0 && selectedTier.prices?.USD?.monthly === 0));
  // Sincroniza isFree no state para que STEPS seja consistente entre renders
  React.useEffect(() => { setIsFree(!!isFreeCalc); }, [form.pendingTier, tiers.length]);
  const STEPS = isFree ? ['company', 'plan', 'done'] : ['company', 'plan', 'payment', 'done'];
  const lastPayStep = STEPS.length - 2; // último step antes de done

  return (
    <Page>
      <Glow />
      <Container>
        <Logo>
          <LogoImg src={logoColor} alt="Libben Agents" />
        </Logo>

        <Card>
          {step < STEPS.length - 1 && (
            <>
              <CardTitle>{t('title')}</CardTitle>
              <CardSubtitle>{t('subtitle')}</CardSubtitle>

              <StepBar>
                {STEPS.slice(0, STEPS.length - 1).map((s, i) => (
                  <React.Fragment key={s}>
                    <StepDot $done={step > i} $active={step === i}>
                      {step > i ? <Check size={12} /> : i + 1}
                    </StepDot>
                    <StepLabel $active={step === i}>{t(`steps.${s}`)}</StepLabel>
                    {i < STEPS.length - 2 && <StepLine $done={step > i} />}
                  </React.Fragment>
                ))}
              </StepBar>
            </>
          )}

          {/* ── Verificação de email (antes do step 0) ── */}
          {emailVerifyStep && (
            <Fields>
              <SectionLabel>{t('emailVerifyTitle', { defaultValue: 'Verificar email' })}</SectionLabel>
              <p style={{ fontSize: theme.typography.sm, color: theme.colors.textMuted, margin: 0 }}>
                {t('emailVerifySubtitle', { defaultValue: 'Vamos verificar seu email antes de continuar.' })}
              </p>
              <Field>
                <FLabel>{t('email')}</FLabel>
                <FInput
                  type="email"
                  value={verifyEmail}
                  onChange={e => setVerifyEmail(e.target.value)}
                  placeholder={t('emailPlaceholder')}
                  disabled={codeSent}
                  autoFocus={!codeSent}
                />
              </Field>
              {codeSent && (
                <Field>
                  <FLabel>{t('verifyCode', { defaultValue: 'Código recebido por email' })}</FLabel>
                  <FInput
                    value={verifyCode}
                    onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    style={{ textAlign: 'center', fontSize: '1.4rem', letterSpacing: '0.3em' }}
                    autoFocus
                    onKeyDown={e => e.key === 'Enter' && handleVerifyCode()}
                  />
                </Field>
              )}
              {error && <ErrorBox>{error}</ErrorBox>}
              {!codeSent ? (
                <NextBtn onClick={handleSendCode} disabled={loading}>
                  {loading ? <><Loader size={14} className="spin" /> {t('sending', { defaultValue: 'Enviando...' })}</> : t('sendCode', { defaultValue: 'Enviar código' })}
                </NextBtn>
              ) : (
                <>
                  <NextBtn onClick={handleVerifyCode} disabled={loading || verifyCode.length < 6}>
                    {loading ? <><Loader size={14} className="spin" /> {t('verifying', { defaultValue: 'Verificando...' })}</> : t('confirmCode', { defaultValue: 'Confirmar código' })}
                  </NextBtn>
                  <BackBtn aria-label={t('common:back')} type="button" onClick={async () => {
                    setResendMsg(''); setResendLoading(true);
                    try {
                      const res = await fetch(`${API_URL}/auth/send-code`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email: verifyEmail })
                      });
                      const data = await res.json();
                      setResendMsg(res.ok ? t('resendSuccess', { defaultValue: 'Código reenviado!' }) : data.message);
                    } catch { setResendMsg(t('errorGeneric')); }
                    finally { setResendLoading(false); }
                  }} disabled={resendLoading}>
                    {resendLoading ? <Loader size={14} className="spin" /> : t('resendCode', { defaultValue: 'Reenviar código' })}
                  </BackBtn>
                  {resendMsg && <div style={{ textAlign: 'center', fontSize: '12px', color: theme.colors.textMuted }}>{resendMsg}</div>}
                </>
              )}
            </Fields>
          )}

          {/* ── Step 0: Empresa / Pessoa ── */}
          {!emailVerifyStep && step === 0 && (
            <Fields>
              <SectionLabel><Building2 size={14} /> {t(`step1Title_${form.personType}`)}</SectionLabel>
              <Row>
                <Field>
                  <FLabel>{t('personType')}</FLabel>
                  <FSelect value={form.personType} onChange={e => { set('personType', e.target.value); set('document', ''); }}>
                    <option value="company">{t('personTypeCompany')}</option>
                    <option value="individual">{t('personTypeIndividual')}</option>
                  </FSelect>
                </Field>
                <Field>
                  <FLabel>{t('country')}</FLabel>
                  <FSelect value={form.country} onChange={e => { set('country', e.target.value); set('document', ''); }}>
                    {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.flag} {t(`countries.${c.code}`, { defaultValue: c.name })}</option>)}
                  </FSelect>
                </Field>
              </Row>
              <Row>
                <Field>
                  <FLabel>{t(`entityName_${form.personType}`)}</FLabel>
                  <FInput value={form.name} onChange={e => set('name', e.target.value)} placeholder={t(`entityNamePlaceholder_${form.personType}`)} />
                </Field>
                <Field>
                  <FLabel>{t(`entityEmail_${form.personType}`)}</FLabel>
                  <FInput type="email" value={form.email} onChange={e => set('email', e.target.value)} onBlur={e => validateField('email', e.target.value)} placeholder={t(`entityEmailPlaceholder_${form.personType}`)} $error={!!fieldErrors.email} />
                  {fieldErrors.email && <FFieldError>{fieldErrors.email}</FFieldError>}
                </Field>
              </Row>
              <Field>
                <FLabel>{t('adminPassword')}</FLabel>
                <FInput type="password" value={form.adminPassword} onChange={e => set('adminPassword', e.target.value)} onBlur={e => validateField('adminPassword', e.target.value)} placeholder={t('adminPasswordPlaceholder')} $error={!!fieldErrors.adminPassword} />
                {fieldErrors.adminPassword && <FFieldError>{fieldErrors.adminPassword}</FFieldError>}
                {form.adminPassword && form.adminPassword.length >= 8 && !fieldErrors.adminPassword && (
                  <FFieldSuccess>{t('passwordValid', { defaultValue: '✓ Senha válida' })}</FFieldSuccess>
                )}
              </Field>
            </Fields>
          )}

          {/* ── Step 1: Plano ── */}
          {!emailVerifyStep && step === 1 && (
            <Fields>
              <SectionLabel><Package size={14} /> {t('step2Title')}</SectionLabel>
              <PlanSubtitle>{(() => {
                const selTier = tiers.find(t => t.tierId === form.pendingTier);
                if (!selTier) return t('step2SubtitleNoTrial', { defaultValue: 'A cobrança será realizada na ativação da conta.' });
                if (selTier.trialDays > 0) return t('step2Subtitle');
                return t('step2SubtitleNoTrial', { defaultValue: 'A cobrança será realizada na ativação da conta.' });
              })()}</PlanSubtitle>
              <AnnualToggleRow>
                {(() => {
                  const selectedTierObj = tiers.find(t => t.tierId === form.pendingTier);
                  const hasAnnualPrice  = !!(selectedTierObj?.prices?.[form.currency]?.annual);
                  return (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: hasAnnualPrice ? 'pointer' : 'not-allowed', fontSize: '14px', opacity: hasAnnualPrice ? 1 : 0.4 }}>
                      <input
                        type="checkbox"
                        checked={annualBilling}
                        disabled={!hasAnnualPrice}
                        onChange={e => hasAnnualPrice && setAnnualBilling(e.target.checked)}
                      />
                      <span>{t('annualBilling', { defaultValue: 'Cobrança anual' })} <strong style={{ color: theme.colors.success }}>{t('annualDiscount', { defaultValue: '2 meses grátis' })}</strong></span>
                      {!hasAnnualPrice && form.pendingTier && <span style={{ fontSize: '12px', color: theme.colors.gray400 }}>{t('annualNotAvailable', { defaultValue: 'Não disponível neste plano' })}</span>}
                    </label>
                  );
                })()}
              </AnnualToggleRow>
              <TierGrid>
                {tiers.map(tier => {
                  const price = annualBilling
                    ? (tier.prices?.[form.currency]?.annual ?? tier.prices?.[form.currency]?.monthly)
                    : tier.prices?.[form.currency]?.monthly;
                  const lim   = tier.limits || {};
                  const selected = form.pendingTier === tier.tierId;
                  return (
                    <TierCard key={tier.tierId} $selected={selected} onClick={() => { set('pendingTier', tier.tierId); if (!tier.prices?.[form.currency]?.annual) setAnnualBilling(false); }}>
                      <TierRadio $selected={selected}>{selected && <Check size={10} />}</TierRadio>
                      <TierName>{tier.name}</TierName>
                      {tier.trialDays > 0 && <TrialBadge>{t('trialBadge', { days: tier.trialDays, defaultValue: `${tier.trialDays} dias grátis` })}</TrialBadge>}
                      <TierPrice>
                        {tier.trialDays > 0 && <span>{t('thenLabel')}</span>}
                        <strong>{formatPrice(price, form.currency)}</strong>
                        <span>{t('perMonth')}</span>
                      </TierPrice>
                      <TierLimits>
                        <li>{lim.conversations === -1 ? t('unlimited') : lim.conversations} {t('conversations')}</li>
                        <li>{lim.agents === -1 ? t('unlimited') : lim.agents} {lim.agents === 1 ? t('agents') : t('agents_plural')}</li>
                        <li>{lim.channels === -1 ? t('unlimited') : lim.channels} {lim.channels === 1 ? t('channels') : t('channels_plural')}</li>
                      </TierLimits>
                    </TierCard>
                  );
                })}
              </TierGrid>
            </Fields>
          )}

          {/* ── Step 2: Pagamento ── */}
          {!emailVerifyStep && step === 2 && (
            <Fields>
              <SectionLabel><CreditCard size={14} /> {t('step3Title')}</SectionLabel>
              <PlanSubtitle>{t('step3Subtitle')}</PlanSubtitle>
              {selectedTier && (
                <SelectedPlanBanner>
                  <strong>{selectedTier.name}</strong> — R$ {selectedTier.prices?.[form.currency]?.monthly}{t('perMonth')} {t('thenLabel').toLowerCase()} trial
                </SelectedPlanBanner>
              )}
              {/* Dados complementares movidos do step 0 */}
              <Row>
                <Field>
                  <FLabel>{t('phone')}</FLabel>
                  <FInput value={form.phone} onChange={e => set('phone', e.target.value)} placeholder={t('phonePlaceholder')} />
                </Field>
                <Field>
                  {(() => {
                    const docCfg = getDocumentConfig(form.country, form.personType);
                    return <>
                      <FLabel>{docCfg.label}{!docCfg.required && ` (${t('documentOptional')})`}</FLabel>
                      <FInput value={form.document} onChange={e => set('document', docCfg.mask(e.target.value))} placeholder={docCfg.placeholder} />
                    </>;
                  })()}
                </Field>
              </Row>
              <Row>
                <Field>
                  <FLabel>{t('city')}</FLabel>
                  <FInput value={form.city} onChange={e => set('city', e.target.value)} placeholder={t('cityPlaceholder')} />
                </Field>
                <Field>
                  <FLabel>{t('state')}</FLabel>
                  <FInput value={form.state} onChange={e => set('state', e.target.value)} placeholder={t('statePlaceholder')} />
                </Field>
              </Row>
              {form.country === 'BR' && (
                <Row>
                  <Field>
                    <FLabel>{t('postalCode')}</FLabel>
                    <FInput value={form.postalCode} onChange={e => set('postalCode', e.target.value.replace(/\D/g,'').slice(0,8).replace(/(\d{5})(\d)/,'$1-$2'))} placeholder="00000-000" maxLength={9} />
                  </Field>
                  <Field>
                    <FLabel>{t('addressNumber')}</FLabel>
                    <FInput value={form.addressNumber} onChange={e => set('addressNumber', e.target.value)} placeholder="123" />
                  </Field>
                </Row>
              )}
              <Field>
                <FLabel>{t('cardDetails', { defaultValue: 'Dados do cartão' })}</FLabel>
                <StripeCardWrapper>
                  <CardElement options={{ style: { base: { fontSize: '14px', color: theme.colors.textPrimary, '::placeholder': { color: theme.colors.gray400 } } } }} />
                </StripeCardWrapper>
              </Field>
              <SecureNote>{t('securePayment')}</SecureNote>

              {/* ── Aceite dos Termos ── */}
              <TermsRow>
                <TermsCheckbox
                  type="checkbox"
                  id="terms-accept"
                  checked={termsAccepted}
                  onChange={e => setTermsAccepted(e.target.checked)}
                />
                <TermsLabel htmlFor="terms-accept">
                  {t('termsPrefix')}{' '}
                  <TermsLink type="button" onClick={() => setTermsOpen(true)}>
                    <FileText size={12} /> {t('termsLinkText')}
                  </TermsLink>
                  {' '}{t('termsSuffix')}
                </TermsLabel>
              </TermsRow>
            </Fields>
          )}

          {/* ── Modal Termos ── */}
          {termsOpen && (
            <TermsOverlay onClick={() => setTermsOpen(false)}>
              <TermsModal onClick={e => e.stopPropagation()}>
                <TermsHeader>
                  <TermsTitle><FileText size={16} /> {tTerms('pageTitle', { lng: termsLang })}</TermsTitle>
                  <CloseBtn onClick={() => setTermsOpen(false)}><X size={18} /></CloseBtn>
                </TermsHeader>
                <TermsBody ref={termsBodyRef} onScroll={handleTermsScroll}>
                  <TermsContent>
                    {Array.isArray(termsSections) && termsSections.map((section, i) => (
                      <div key={i}>
                        <h3>{section.title}</h3>
                        <p>{section.content}</p>
                      </div>
                    ))}
                    <p style={{ marginTop: '24px', fontSize: '12px', color: theme.colors.textMuted }}>
                      {tTerms('fullDocNote', { lng: termsLang })}{' '}
                      <a href={`${process.env.REACT_APP_SITE_URL || ''}/terms`} target="_blank" rel="noreferrer">libbenagents.com/terms</a>
                    </p>
                  </TermsContent>
                </TermsBody>
                <TermsFooter $scrolled={termsScrolled}>
                  {!termsScrolled && (
                    <ScrollHint>↓ {tTerms('termsScrollHint', { lng: termsLang, ns: 'register' }) || t('termsScrollHint')}</ScrollHint>
                  )}
                  <TermsAcceptBtn
                    disabled={!termsScrolled}
                    onClick={() => { setTermsAccepted(true); setTermsAcceptedAt(new Date().toISOString()); setTermsOpen(false); }}
                  >
                    <Check size={14} /> {t('termsAcceptBtn')}
                  </TermsAcceptBtn>
                </TermsFooter>
              </TermsModal>
            </TermsOverlay>
          )}

          {/* ── Sucesso / Confirmação de email ── */}
          {!emailVerifyStep && step === STEPS.length - 1 && result && (
            confirming ? (
              <SuccessBox>
                <SuccessIcon>📧</SuccessIcon>
                <SuccessTitle>{t('confirmEmailTitle', { defaultValue: 'Verifique seu email' })}</SuccessTitle>
                <SuccessSubtitle>{t('confirmEmailDesc', { defaultValue: `Enviamos um código de verificação para ${pendingEmail}. Digite abaixo para ativar sua conta.` })}</SuccessSubtitle>
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <FInput
                    value={confirmCode}
                    onChange={e => setConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder={t('confirmCodePlaceholder', { defaultValue: 'Código de 6 dígitos' })}
                    style={{ textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.3em' }}
                    maxLength={6}
                    autoFocus
                  />
                  {error && <ErrorBox>{error}</ErrorBox>}
                  <NextBtn
                    onClick={async () => {
                      setError('');
                      setLoading(true);
                      try {
                        const res = await cognitoAuth.confirmSignUp(pendingEmail, confirmCode);
                        if (!res.success) { setError(res.error); return; }
                        const loginResult = await cognitoAuth.signIn(pendingEmail, pendingPassword);
                        if (loginResult.success) localStorage.setItem('user', JSON.stringify(loginResult.user));
                        setConfirming(false);
                      } catch { setError(t('errorGeneric')); }
                      finally { setLoading(false); }
                    }}
                    disabled={loading || confirmCode.length < 6}
                    style={{ width: '100%' }}
                  >
                    {loading ? <><Loader size={14} className="spin" /> {t('saving', { ns: 'common' })}</> : t('confirmCodeBtn', { defaultValue: 'Confirmar email' })}
                  </NextBtn>
                  <BackBtn
                    type="button"
                    onClick={async () => {
                      setResendMsg('');
                      setResendLoading(true);
                      const res = await cognitoAuth.resendConfirmationCode(pendingEmail);
                      setResendLoading(false);
                      setResendMsg(res.success ? t('resendSuccess', { defaultValue: 'Código reenviado!' }) : res.error);
                    }}
                    disabled={resendLoading}
                  >
                    {resendLoading ? <Loader size={14} className="spin" /> : t('resendCode', { defaultValue: 'Reenviar código' })}
                  </BackBtn>
                  {resendMsg && <div style={{ textAlign: 'center', fontSize: '12px', color: theme.colors.textMuted }}>{resendMsg}</div>}
                </div>
              </SuccessBox>
            ) : (
              <SuccessBox>
                <SuccessIcon>🎉</SuccessIcon>
                <SuccessTitle>{t('successTitle')}</SuccessTitle>
                <SuccessSubtitle>
                  {result.isFree ? t('successSubtitleFree', { defaultValue: 'Sua conta gratuita está pronta!' }) : t('successSubtitle', { days: result.trialDays || 7 })}
                </SuccessSubtitle>
                <SuccessDetails>
                  {!result.isFree && result.trialEndsAt && (
                    <SuccessRow>
                      <span>{t('successTrialEnds')}</span>
                      <strong>{new Date(result.trialEndsAt).toLocaleDateString(i18n.language)}</strong>
                    </SuccessRow>
                  )}
                  <SuccessRow>
                    <span>{t('successPlan')}</span>
                    <strong>{tiers.find(t => t.tierId === (result.pendingTier || form.pendingTier))?.name || result.pendingTier}</strong>
                  </SuccessRow>
                </SuccessDetails>
                <CtaBtn onClick={() => navigate(`/dashboard/${result.tenantId}`)}>
                  {t('successCta')} →
                </CtaBtn>
              </SuccessBox>
            )
          )}

          {error && !emailVerifyStep && <ErrorBox>{error}</ErrorBox>}

          {!emailVerifyStep && step < STEPS.length - 1 && (
            <Actions>
              {step > 0 && (
                <BackBtn aria-label={t('back', { ns: 'common' })} type="button" onClick={() => { setStep(s => s - 1); setError(''); }}>
                  ← {t('back', { ns: 'common' })}
                </BackBtn>
              )}
              <div style={{ flex: 1 }} />
              {step < lastPayStep
                ? <NextBtn onClick={handleNext}>{t('next', { ns: 'common' })} →</NextBtn>
                : step === lastPayStep && isFree
                  ? <NextBtn onClick={handleNext} disabled={loading}>
                      {loading ? <><Loader size={14} className="spin" /> {t('saving', { ns: 'common' })}</> : t('successCta')}
                    </NextBtn>
                  : <NextBtn onClick={handleSubmit} disabled={loading}>
                      {loading ? <><Loader size={14} className="spin" /> {t('saving', { ns: 'common' })}</> : t('successCta')}
                    </NextBtn>
              }
            </Actions>
          )}

          <LoginLink>
            {t('alreadyHaveAccount')} <Link to="/login">{t('signIn')}</Link>
          </LoginLink>
        </Card>

        <FooterNote>{t('cancelAnytime')}</FooterNote>
      </Container>
    </Page>
  );
}

// ── Styled ────────────────────────────────────────────────────────────────────

const StripeCardWrapper = styled.div`
  padding: ${theme.spacing[3]}; border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md}; background: white;
  &:focus-within { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 3px ${theme.colors.primaryLight}; }
`;

const Page = styled.div`
  min-height: 100vh;
  background: ${theme.colors.gray900};
  display: flex; align-items: center; justify-content: center;
  padding: ${theme.spacing[6]} ${theme.spacing[4]};
  position: relative; overflow: hidden;
`;

const Glow = styled.div`
  position: absolute; width: 700px; height: 700px; border-radius: 50%;
  background: radial-gradient(circle, ${theme.colors.primary}22 0%, transparent 70%);
  top: -200px; left: 50%; transform: translateX(-50%); pointer-events: none;
`;

const Container = styled.div`
  width: 100%; max-width: 560px;
  display: flex; flex-direction: column; align-items: center; gap: ${theme.spacing[5]};
`;

const Logo = styled.div`display: flex; align-items: center; justify-content: center;`;
const LogoImg = styled.img`
  height: 44px;
  width: auto;
  object-fit: contain;
`;

const Card = styled.div`
  width: 100%; background: ${theme.colors.white};
  border-radius: ${theme.radii['2xl']}; padding: ${theme.spacing[8]};
  box-shadow: ${theme.shadows.xl};
`;

const CardTitle = styled.h1`
  font-size: ${theme.typography['2xl']}; font-weight: ${theme.typography.bold};
  color: ${theme.colors.textPrimary}; margin: 0 0 ${theme.spacing[1]} 0; text-align: center;
`;
const CardSubtitle = styled.p`
  font-size: ${theme.typography.sm}; color: ${theme.colors.textMuted};
  text-align: center; margin: 0 0 ${theme.spacing[6]} 0;
`;

const StepBar = styled.div`
  display: flex; align-items: center; margin-bottom: ${theme.spacing[8]};
`;
const StepDot = styled.div`
  width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 600;
  background: ${p => p.$done ? theme.colors.success : p.$active ? theme.colors.primary : theme.colors.gray200};
  color: ${p => (p.$done || p.$active) ? '#fff' : theme.colors.textMuted};
`;
const StepLabel = styled.span`
  font-size: ${theme.typography.xs}; margin: 0 ${theme.spacing[2]};
  color: ${p => p.$active ? theme.colors.primary : theme.colors.textMuted};
  font-weight: ${p => p.$active ? 600 : 400};
`;
const StepLine = styled.div`
  flex: 1; height: 2px;
  background: ${p => p.$done ? theme.colors.success : theme.colors.gray200};
`;

const Fields = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing[4]};`;
const SectionLabel = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[2]};
  font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textSecondary}; text-transform: uppercase; letter-spacing: 0.05em;
`;
const Row = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: ${theme.spacing[4]};`;
const Field = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing[1]};`;
const FLabel = styled.label`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.medium}; color: ${theme.colors.textPrimary};`;
const FInput = styled.input`
  padding: ${theme.spacing[3]}; border: 1px solid ${p => p.$error ? theme.colors.danger : theme.colors.border};
  border-radius: ${theme.radii.md}; font-size: ${theme.typography.sm};
  font-family: inherit; outline: none; transition: ${theme.transitions.fast};
  &:focus { border-color: ${p => p.$error ? theme.colors.danger : theme.colors.primary}; box-shadow: 0 0 0 3px ${p => p.$error ? theme.colors.dangerLight : theme.colors.primaryLight}; }
  &::placeholder { color: ${theme.colors.textMuted}; }
`;
const FFieldError = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.danger}; margin-top: 2px;`;
const FFieldSuccess = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.success}; margin-top: 2px;`;
const FSelect = styled.select`
  padding: ${theme.spacing[3]}; border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md}; font-size: ${theme.typography.sm};
  font-family: inherit; outline: none; background: white; width: 100%;
  &:focus { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 3px ${theme.colors.primaryLight}; }
`;
const Divider = styled.div`height: 1px; background: ${theme.colors.border}; margin: ${theme.spacing[2]} 0;`;

const PlanSubtitle = styled.p`font-size: ${theme.typography.sm}; color: ${theme.colors.textMuted}; margin: 0;`;
const TierGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: ${theme.spacing[3]};`;
const TierCard = styled.div`
  border: 2px solid ${p => p.$selected ? theme.colors.primary : theme.colors.border};
  border-radius: ${theme.radii.xl}; padding: ${theme.spacing[4]};
  cursor: pointer; background: ${p => p.$selected ? theme.colors.primaryLight : theme.colors.white};
  transition: ${theme.transitions.base}; position: relative;
  &:hover { border-color: ${theme.colors.primary}; }
`;
const TierRadio = styled.div`
  position: absolute; top: ${theme.spacing[3]}; right: ${theme.spacing[3]};
  width: 18px; height: 18px; border-radius: 50%;
  border: 2px solid ${p => p.$selected ? theme.colors.primary : theme.colors.border};
  background: ${p => p.$selected ? theme.colors.primary : 'white'};
  color: white; display: flex; align-items: center; justify-content: center;
`;
const TierName = styled.div`font-size: ${theme.typography.base}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary}; margin-bottom: ${theme.spacing[2]};`;
const TrialBadge = styled.div`
  display: inline-block; padding: 2px ${theme.spacing[2]};
  background: ${theme.colors.successLight}; color: ${theme.colors.success};
  border: 1px solid ${theme.colors.successBorder};
  border-radius: ${theme.radii.full}; font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.semibold}; margin-bottom: ${theme.spacing[3]};
`;
const TierPrice = styled.div`
  display: flex; align-items: baseline; gap: 4px;
  font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};
  margin-bottom: ${theme.spacing[3]};
  strong { font-size: ${theme.typography.lg}; color: ${theme.colors.textPrimary}; font-weight: 700; }
`;
const TierLimits = styled.ul`
  list-style: none; padding: 0; margin: 0;
  display: flex; flex-direction: column; gap: ${theme.spacing[1]};
  font-size: ${theme.typography.xs}; color: ${theme.colors.textSecondary};
  li::before { content: '✓ '; color: ${theme.colors.success}; }
`;

const SelectedPlanBanner = styled.div`
  padding: ${theme.spacing[3]}; background: ${theme.colors.primaryLight};
  border: 1px solid ${theme.colors.primaryBorder}; border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm}; color: ${theme.colors.primary};
`;
const SecureNote = styled.div`
  font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; text-align: center;
`;

const TermsRow = styled.div`
  display: flex; align-items: flex-start; gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]}; background: ${theme.colors.gray50};
  border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md};
`;
const TermsCheckbox = styled.input`
  margin-top: 2px; flex-shrink: 0; cursor: pointer;
  width: 16px; height: 16px; accent-color: ${theme.colors.primary};
`;
const TermsLabel = styled.label`
  font-size: ${theme.typography.sm}; color: ${theme.colors.textSecondary};
  line-height: 1.5; cursor: pointer;
`;
const TermsLink = styled.button`
  background: none; border: none; padding: 0;
  color: ${theme.colors.primary}; font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.medium}; cursor: pointer; text-decoration: underline;
  display: inline-flex; align-items: center; gap: 3px;
  &:hover { color: ${theme.colors.primaryHover}; }
`;

const TermsOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.6);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000; padding: ${theme.spacing[4]};
`;
const TermsModal = styled.div`
  background: white; border-radius: ${theme.radii.xl};
  width: 100%; max-width: 600px; max-height: 80vh;
  display: flex; flex-direction: column;
  box-shadow: ${theme.shadows.xl};
`;
const TermsHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  border-bottom: 1px solid ${theme.colors.border};
`;
const TermsTitle = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[2]};
  font-size: ${theme.typography.base}; font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;
const CloseBtn = styled.button`
  background: none; border: none; cursor: pointer;
  color: ${theme.colors.textMuted}; padding: ${theme.spacing[1]};
  border-radius: ${theme.radii.md};
  &:hover { background: ${theme.colors.gray100}; color: ${theme.colors.textPrimary}; }
`;
const TermsBody = styled.div`
  flex: 1; overflow-y: auto; padding: ${theme.spacing[5]};
  scroll-behavior: smooth;
`;
const TermsContent = styled.div`
  font-size: ${theme.typography.sm}; color: ${theme.colors.textSecondary};
  line-height: 1.7;
  h3 { font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold};
       color: ${theme.colors.textPrimary}; margin: ${theme.spacing[4]} 0 ${theme.spacing[2]} 0; }
  h3:first-child { margin-top: 0; }
  p { margin: 0 0 ${theme.spacing[2]} 0; }
  a { color: ${theme.colors.primary}; }
`;
const TermsFooter = styled.div`
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  border-top: 1px solid ${theme.colors.border};
  display: flex; flex-direction: column; align-items: stretch; gap: ${theme.spacing[2]};
  background: ${p => p.$scrolled ? theme.colors.gray50 : 'white'};
  transition: background 0.3s;
`;
const ScrollHint = styled.div`
  font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; text-align: center;
`;
const TermsAcceptBtn = styled.button`
  display: flex; align-items: center; justify-content: center; gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]};
  background: ${p => p.disabled ? theme.colors.gray200 : theme.colors.primary};
  color: ${p => p.disabled ? theme.colors.textMuted : 'white'};
  border: none; border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold};
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  transition: ${theme.transitions.fast};
  &:hover:not(:disabled) { background: ${theme.colors.primaryHover}; }
`;

const SuccessBox = styled.div`display: flex; flex-direction: column; align-items: center; gap: ${theme.spacing[4]}; padding: ${theme.spacing[4]} 0;`;
const SuccessIcon = styled.div`font-size: 56px;`;
const SuccessTitle = styled.h2`font-size: ${theme.typography['2xl']}; font-weight: ${theme.typography.bold}; color: ${theme.colors.textPrimary}; margin: 0; text-align: center;`;
const SuccessSubtitle = styled.p`font-size: ${theme.typography.base}; color: ${theme.colors.textMuted}; margin: 0; text-align: center;`;
const SuccessDetails = styled.div`
  width: 100%; background: ${theme.colors.gray50}; border-radius: ${theme.radii.lg};
  padding: ${theme.spacing[4]}; display: flex; flex-direction: column; gap: ${theme.spacing[3]};
`;
const SuccessRow = styled.div`display: flex; justify-content: space-between; font-size: ${theme.typography.sm}; color: ${theme.colors.textSecondary};`;
const CtaBtn = styled.button`
  width: 100%; padding: ${theme.spacing[4]};
  background: ${theme.colors.primary}; color: white;
  border: none; border-radius: ${theme.radii.lg};
  font-size: ${theme.typography.base}; font-weight: ${theme.typography.semibold};
  cursor: pointer; transition: ${theme.transitions.fast};
  &:hover { background: ${theme.colors.primaryHover}; }
`;

const ErrorBox = styled.div`
  margin-top: ${theme.spacing[4]}; padding: ${theme.spacing[3]};
  background: ${theme.colors.dangerLight}; border: 1px solid ${theme.colors.dangerBorder};
  border-radius: ${theme.radii.md}; color: ${theme.colors.danger};
  font-size: ${theme.typography.sm}; text-align: center;
`;

const Actions = styled.div`display: flex; align-items: center; gap: ${theme.spacing[3]}; margin-top: ${theme.spacing[6]};`;
const BackBtn = styled.button`
  background: none; border: none; color: ${theme.colors.textMuted};
  font-size: ${theme.typography.sm}; cursor: pointer;
  &:hover { color: ${theme.colors.textPrimary}; }
`;
const NextBtn = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary}; color: white;
  border: none; border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold};
  cursor: pointer; transition: ${theme.transitions.fast};
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { background: ${theme.colors.primaryHover}; }
  .spin { animation: spin 0.8s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const LoginLink = styled.div`
  text-align: center; margin-top: ${theme.spacing[5]};
  font-size: ${theme.typography.sm}; color: ${theme.colors.textMuted};
  a { color: ${theme.colors.primary}; font-weight: ${theme.typography.medium}; }
`;

const FooterNote = styled.div`
  font-size: ${theme.typography.xs}; color: ${theme.colors.gray500}; text-align: center;
`;

// ── Path selection styles ─────────────────────────────────────────────────────

const AnnualToggleRow = styled.div`
  padding: ${theme.spacing[2]} 0;
`;

const PathPage = styled.div`
  min-height: 100vh; background: ${theme.colors.gray900};
  display: flex; align-items: center; justify-content: center;
  padding: ${theme.spacing[6]} ${theme.spacing[4]}; position: relative; overflow: hidden;
`;
const PathGlow = styled.div`
  position: absolute; width: 700px; height: 700px; border-radius: 50%;
  background: radial-gradient(circle, ${theme.colors.primary}22 0%, transparent 70%);
  top: -200px; left: 50%; transform: translateX(-50%); pointer-events: none;
`;
const PathContainer = styled.div`
  width: 100%; max-width: 480px;
  display: flex; flex-direction: column; align-items: center; gap: ${theme.spacing[5]};
`;
const PathLogo = styled.img`height: 40px; width: auto; object-fit: contain;`;
const PathTitle = styled.h1`
  font-size: ${theme.typography['2xl']}; font-weight: ${theme.typography.bold};
  color: white; margin: 0; text-align: center;
`;
const PathSubtitle = styled.p`
  font-size: ${theme.typography.base}; color: ${theme.colors.gray400};
  margin: 0; text-align: center;
`;
const PathCards = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: ${theme.spacing[4]}; width: 100%;`;
const PathCard = styled.div`
  background: ${theme.colors.white}; border-radius: ${theme.radii['2xl']};
  padding: ${theme.spacing[6]}; cursor: pointer;
  display: flex; flex-direction: column; gap: ${theme.spacing[2]};
  transition: ${theme.transitions.base}; border: 2px solid transparent;
  &:hover { border-color: ${theme.colors.primary}; transform: translateY(-2px); box-shadow: ${theme.shadows.lg}; }
`;
const PathIcon = styled.div`font-size: 36px;`;
const PathCardTitle = styled.div`font-size: ${theme.typography.lg}; font-weight: ${theme.typography.bold}; color: ${theme.colors.textPrimary};`;
const PathCardDesc = styled.div`font-size: ${theme.typography.sm}; color: ${theme.colors.textMuted}; line-height: 1.5; flex: 1;`;
const PathCardCta = styled.div`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.primary}; margin-top: ${theme.spacing[2]};`;
const PathFooter = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.gray500}; a { color: ${theme.colors.primary}; }`;

const PathDivider = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[3]}; width: 100%;
  color: ${theme.colors.gray500}; font-size: ${theme.typography.xs};
  &::before, &::after { content: ''; flex: 1; height: 1px; background: rgba(255,255,255,0.1); }
`;

const PathGoogleBtn = styled.a`
  display: flex; align-items: center; justify-content: center; gap: ${theme.spacing[2]};
  width: 100%; padding: ${theme.spacing[3]};
  background: white; border: 1px solid rgba(255,255,255,0.2);
  border-radius: ${theme.radii.xl}; font-size: ${theme.typography.sm};
  color: ${theme.colors.textPrimary}; text-decoration: none; cursor: pointer;
  font-weight: ${theme.typography.medium};
  transition: ${theme.transitions.base};
  &:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.15); transform: translateY(-1px); }
`;
